import {
  DashboardTileReason,
  DashboardTileStatus,
} from "../../../services/zeos-api/contracts/Dashboard";
import { ExploreSurveyDomainSlug } from "../../../configs/ExploreSurveyConfig";

export type DashboardTileKey =
  | "building"
  | "solar"
  | "ev"
  | "carbon"
  | "battery"
  | "dr";

/** Where an unlocked tile CTA should go. `none` = banner only (already connected / nothing to join). */
export type DashboardCtaAction = "derapi" | "utility" | "explore" | "none";

export const hasDashboardConnectCta = (action: DashboardCtaAction) =>
  action === "derapi" || action === "utility";

export interface IDashboardTileStatusCopy {
  heading: string;
  body: string;
  ctaLabel: string;
  exploreDomain: ExploreSurveyDomainSlug;
  ctaAction: DashboardCtaAction;
}

const DEFAULT_ERROR: Omit<IDashboardTileStatusCopy, "exploreDomain"> = {
  heading: "Something went wrong",
  body: "We couldn't load data for this tile. Try again later.",
  ctaLabel: "Explore options",
  ctaAction: "explore",
};

const FILLING_PARTIAL_MESSAGE = /filling|still loading/i;
const ESTIMATED_PARTIAL_MESSAGE = /estimated|default emission factor/i;

const estimatedPartialHeading = (tileKey: DashboardTileKey): string => {
  switch (tileKey) {
    case "solar":
      return "Estimated bill-credit rate";
    case "carbon":
      return "Estimated emission factor";
    default:
      return "Partial data";
  }
};

const normalizeCopy = (text: string): string =>
  text.trim().replace(/[.!\s]+$/, "").toLowerCase();

/** Filling stays filling; estimated-only fallback must not read as still-loading. */
const resolvePartialHeading = (
  tileKey: DashboardTileKey,
  defaultHeading: string,
  message?: string | null
): string => {
  // Reason-coded partials (REASON_COPY) are resolved before this is reached; the two regexes below
  // are older cases that have no code yet.
  if (!message) {
    return defaultHeading;
  }
  const filling = FILLING_PARTIAL_MESSAGE.test(message);
  const estimated = ESTIMATED_PARTIAL_MESSAGE.test(message);
  if (estimated && !filling) {
    return estimatedPartialHeading(tileKey);
  }
  return defaultHeading;
};

/**
 * Partials the API explains with a reason code. The code picks the heading and CTA, so the API's
 * message is free to be reworded; that message is the body (it can carry specifics such as the
 * last bill month), with the body here only as the fallback. 4102 added the Building no-bills
 * reasons beyond no_bills_in_period.
 */
const REASON_COPY: Record<
  DashboardTileReason,
  Omit<IDashboardTileStatusCopy, "exploreDomain">
> = {
  utility_link_stale: {
    heading: "Utility connection needs attention",
    body: "We haven't received new bills from your utility recently. Reconnect your utility account to resume updates.",
    ctaLabel: "Reconnect utility",
    ctaAction: "utility",
  },
  meters_being_confirmed: {
    heading: "Meters being confirmed",
    body: "Your utility is linked and your meter setup is being confirmed. Your figures will appear here once that's done.",
    ctaLabel: "",
    ctaAction: "none",
  },
  meters_connecting: {
    heading: "Meters are connecting",
    body: "Your utility is linked and we're collecting your bills. This can take up to a week. Figures will appear here as they arrive.",
    ctaLabel: "",
    ctaAction: "none",
  },
  no_bills_in_period: {
    heading: "No bills for this period",
    body: "Your utility is connected, but there are no bills in the selected range. Try a longer range.",
    ctaLabel: "",
    ctaAction: "none",
  },
};

const isReasonCoded = (reason?: string | null): reason is DashboardTileReason =>
  !!reason && Object.prototype.hasOwnProperty.call(REASON_COPY, reason);

const TILE_STATUS_COPY: Record<
  DashboardTileKey,
  Partial<Record<DashboardTileStatus, IDashboardTileStatusCopy>>
> = {
  building: {
    not_connected: {
      heading: "Connect your utility",
      body: "Link UtilityAPI or Bayou, or upload bills, to see consumption and savings for this property.",
      ctaLabel: "Connect utility",
      exploreDomain: "building-performance",
      ctaAction: "utility",
    },
    error: {
      ...DEFAULT_ERROR,
      body: "Utility data is temporarily unavailable for this property.",
      ctaLabel: "Connect utility",
      exploreDomain: "building-performance",
      ctaAction: "utility",
    },
    partial: {
      heading: "Partial data",
      body: "Some production months are still loading. Savings will complete as history catches up.",
      ctaLabel: "",
      exploreDomain: "building-performance",
      ctaAction: "none",
    },
  },
  solar: {
    not_connected: {
      heading: "Connect power generation",
      body: "Link a production feed (Derapi / Enphase) to see generation and bill credits.",
      ctaLabel: "Connect generation",
      exploreDomain: "power-generation",
      ctaAction: "derapi",
    },
    error: {
      ...DEFAULT_ERROR,
      body: "Power generation data is temporarily unavailable.",
      exploreDomain: "power-generation",
    },
    partial: {
      heading: "Production history filling",
      body: "Some months are still loading from Derapi. Numbers will complete as history catches up — refresh in a few minutes.",
      ctaLabel: "",
      exploreDomain: "power-generation",
      ctaAction: "none",
    },
  },
  ev: {
    not_connected: {
      heading: "Connect EV charging",
      body: "Link a SWTCH site to see energy, revenue, and charging activity.",
      ctaLabel: "Connect EV",
      exploreDomain: "ev-charging",
      ctaAction: "explore",
    },
    error: {
      ...DEFAULT_ERROR,
      body: "EV charging data is temporarily unavailable.",
      exploreDomain: "ev-charging",
    },
  },
  carbon: {
    not_connected: {
      heading: "Connect production data",
      body: "Carbon savings need a production feed (Derapi / Enphase) for this property.",
      ctaLabel: "Connect data",
      exploreDomain: "carbon-emissions",
      ctaAction: "derapi",
    },
    error: {
      ...DEFAULT_ERROR,
      body: "Carbon emissions data is temporarily unavailable.",
      exploreDomain: "carbon-emissions",
    },
    partial: {
      heading: "Carbon history filling",
      body: "Some months are still loading from Derapi. Carbon figures will complete as production history catches up — refresh in a few minutes.",
      ctaLabel: "",
      exploreDomain: "carbon-emissions",
      ctaAction: "none",
    },
  },
  battery: {
    no_asset: {
      heading: "Battery not configured",
      body: "No battery energy storage is connected for this property yet.",
      ctaLabel: "Explore battery storage",
      exploreDomain: "battery-storage",
      ctaAction: "explore",
    },
    not_connected: {
      heading: "Battery not configured",
      body: "No battery energy storage is connected for this property yet.",
      ctaLabel: "Explore battery storage",
      exploreDomain: "battery-storage",
      ctaAction: "explore",
    },
    error: {
      ...DEFAULT_ERROR,
      body: "Battery status is temporarily unavailable.",
      exploreDomain: "battery-storage",
    },
  },
  dr: {
    // Framework-only placeholder (DB-F5): DR telemetry is not integrated yet, so the tile
    // renders a defined "coming soon" state rather than being absent from the dashboard.
    not_connected: {
      heading: "Demand response coming soon",
      body: "Track demand response enrollment, curtailment, and program revenue here once DR integration is available for this property.",
      ctaLabel: "Notify me",
      exploreDomain: "demand-response",
      ctaAction: "explore",
    },
    error: {
      ...DEFAULT_ERROR,
      body: "Demand response data is temporarily unavailable.",
      exploreDomain: "demand-response",
    },
  },
};

export const getDashboardTileStatusCopy = (
  tileKey: DashboardTileKey,
  status: DashboardTileStatus | undefined,
  fallbackMessage?: string | null,
  reason?: string | null
): IDashboardTileStatusCopy => {
  const byStatus = status ? TILE_STATUS_COPY[tileKey]?.[status] : undefined;
  if (byStatus && status === "partial" && isReasonCoded(reason)) {
    const reasonCopy = REASON_COPY[reason];
    return {
      ...byStatus,
      ...reasonCopy,
      body: fallbackMessage || reasonCopy.body,
    };
  }
  if (byStatus) {
    const heading =
      status === "partial"
        ? resolvePartialHeading(tileKey, byStatus.heading, fallbackMessage)
        : byStatus.heading;
    // The API's filling message ("Production history filling") is just the heading again; using it
    // as the body would print the heading twice and drop the explanatory default body.
    const repeatsHeading =
      !!fallbackMessage && normalizeCopy(fallbackMessage) === normalizeCopy(heading);
    if (
      fallbackMessage &&
      !repeatsHeading &&
      (status === "error" || status === "partial")
    ) {
      return { ...byStatus, heading, body: fallbackMessage };
    }
    if (heading !== byStatus.heading) {
      return { ...byStatus, heading };
    }
    return byStatus;
  }

  const exploreDomain =
    TILE_STATUS_COPY[tileKey]?.not_connected?.exploreDomain ??
    TILE_STATUS_COPY[tileKey]?.no_asset?.exploreDomain ??
    "building-performance";

  return {
    heading: DEFAULT_ERROR.heading,
    body: fallbackMessage || DEFAULT_ERROR.body,
    ctaLabel: DEFAULT_ERROR.ctaLabel,
    exploreDomain,
    ctaAction: DEFAULT_ERROR.ctaAction,
  };
};
